import { useState } from 'react'
import { Box, Button, Divider, FormControl, FormLabel, Grid, IconButton, InputAdornment, OutlinedInput, TextField, Typography } from '@mui/material'
import LogoClient from '../components/Icons/LogoClient'
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Visibility from '@mui/icons-material/Visibility';
import ButtonStyled from '../components/ButtonStyled';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { login, userConfirmForgotPassword, userConfirmSignUp, userForgotPassword, userResendConfirmationCode } from '../services/userService';
import ShapeRightIcon from '../components/Icons/ShapeRightIcon';
import VerificationCodeInput from '../components/VerificationCodeInput';

const formLabelStyle = { color: '#FFF', fontWeight: 'bold', fontSize: { xs: '1rem', display: 'flex' }, pb: '0.2rem' };
const inputStyle = { sx: { fontSize: { xs: '1rem', md: '1rem' }, background: '#353535', borderRadius: '0.25rem', color: 'rgba(255, 255, 255, 0.50)' } };
const formControlStyle = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '0.25rem',
        backgroundColor: '#353535',
        "&.Mui-focused fieldset": {
            borderColor: '#FC581F'
        },
    },
    '& .MuiSvgIcon-root': {
        color: 'white'
    },
    '& .MuiOutlinedInput-root fieldset': {
        borderColor: '#BEBDBD',
    }
};

const defaultErrorValues = {
    emailError: false,
    passwordError: false,
    emailVerificationError: false,
    forgotCodeError: false,
    forgotNewPasswordError: false,
    forgotConfirmPasswordError: false,
};
const requiredValues = ['email', 'password'];
const errorValues = {
    emailError: 'Email',
    passwordError: 'Contraseña',
    emailVerificationError: 'Código de verificación',
    forgotCodeError: 'Código de recuperación',
    forgotNewPasswordError: 'Nueva contraseña',
    forgotConfirmPasswordError: 'Confirmación de nueva contraseña',
};

const LoginPage = () => {
    const [userForm, setUserForm] = useState({
        email: '',
        password: '',
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showForgotNewPassword, setShowForgotNewPassword] = useState(false);
    const [showForgotConfirmPassword, setShowForgotConfirmPassword] = useState(false);
    const [errors, setErrors] = useState(defaultErrorValues);
    const [isLoading, setIsLoading] = useState(false);
    const [emailVerificationCode, setEmailVerificationCode] = useState('');
    const [showVerificationEmail, setShowVerificationEmail] = useState(false);
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [showForgotPasswordReset, setShowForgotPasswordReset] = useState(false);
    const [forgotPasswordForm, setForgotPasswordForm] = useState({
        code: '',
        newPassword: '',
        confirmPassword: ''
    });

    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    const { email, password } = userForm;
    const { emailError, passwordError, emailVerificationError, forgotCodeError, forgotNewPasswordError, forgotConfirmPasswordError } = errors;

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const updateForm = (e) => {
        let inputName = e.target.name;
        let value = e.target.value;

        let NewValue = { [inputName]: value }
        setUserForm({
            ...userForm,
            ...NewValue
        });

        errors[`${inputName}Error`] && setErrors({ ...errors, [`${inputName}Error`]: false });
    };

    const updateForgotPasswordForm = (e) => {
        const inputName = e.target.name;
        const value = e.target.value;

        setForgotPasswordForm({
            ...forgotPasswordForm,
            [inputName]: value
        });

        const errorName = `forgot${inputName.charAt(0).toUpperCase()}${inputName.slice(1)}Error`;
        errors[errorName] && setErrors({ ...errors, [errorName]: false });
    };

    const validateForm = () => {
        let newErrors = {};
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        requiredValues.forEach(key => {
            if (key == 'email') {
                newErrors[`${key}Error`] = !userForm[`${key}`] || !emailRegex.test(userForm[`${key}`]);
            } else {
                newErrors[`${key}Error`] = !userForm[`${key}`];
            }
        });

        for (const [key, value] of Object.entries(newErrors)) {
            if (value) {
                enqueueSnackbar(`Campo ${errorValues[key]} incorrecto.`, {
                    variant: 'error'
                });

                setErrors({
                    ...errors,
                    ...newErrors
                });
                return false;
            }
        }

        return true;
    };

    const OnSubmitForm = async () => {
        if (isLoading) {
            return;
        }

        if (!validateForm()) {
            return;
        }

        setIsLoading(true);
        const result = await login(userForm);
        setIsLoading(false);
        if (result.AccessToken) {
            localStorage.clear();
            localStorage.setItem('token', result.AccessToken);
            localStorage.setItem('refreshToken', result.RefreshToken);
            localStorage.setItem('UID', result.uid);
            localStorage.setItem('name', result.name);
            if (!!result.userCart) {
                localStorage.setItem('userCart', JSON.stringify(result.userCart));
            }
            navigate('/');
        } else {
            if (result.error?.code === 'UserNotConfirmedException') {
                setShowVerificationEmail(true);
                setShowForgotPassword(false);
                setShowForgotPasswordReset(false);
            }
            setErrors({
                emailError: true,
                passwordError: true
            });
            setUserForm({
                ...userForm,
                password: ''
            });
            enqueueSnackbar(result.message, {
                variant: 'error',
                autoHideDuration: 3000
            });
        }
    };

    const OnOpenForgotPassword = () => {
        setShowVerificationEmail(false);
        setShowForgotPasswordReset(false);
        setShowForgotPassword(true);
    }

    const OnCloseForgotPassword = () => {
        setShowForgotPassword(false);
        setShowForgotPasswordReset(false);
        setForgotPasswordForm({
            code: '',
            newPassword: '',
            confirmPassword: ''
        });
    }

    const OnSubmitVerificationForm = async () => {
        if (!emailVerificationCode) {
            setErrors(prev => ({
                ...prev,
                emailVerificationError: true
            }));
            return;
        }
        setErrors(prev => ({
            ...prev,
            emailVerificationError: false
        }));
        // Add your verification logic here
        setIsLoading(true);
        const resultForm = await userConfirmSignUp(userForm.email, emailVerificationCode);
        setIsLoading(false);

        if (resultForm.result) {
            enqueueSnackbar(resultForm.message, {
                variant: 'success',
                style: {
                    Width: '100%',
                    justifyContent: 'center'
                }
            });
            setShowVerificationEmail(false);
            navigate('/login');
        } else {
            enqueueSnackbar(resultForm.message, {
                variant: 'error'
            });
        }
        setIsLoading(false);
    };

    const OnRequestNewCode = async () => {
        setIsLoading(true);
        const result = await userResendConfirmationCode(userForm.email);
        setIsLoading(false);

        if (result.result) {
            enqueueSnackbar(result.message, {
                variant: 'success',
                style: {
                    Width: '100%',
                    justifyContent: 'center'
                }
            });
        } else {
            enqueueSnackbar(result.message, {
                variant: 'error'
            });
        }
    };

    const OnSubmitForgotPasswordRequest = async () => {
        if (isLoading) {
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!email || !emailRegex.test(email)) {
            setErrors((prev) => ({
                ...prev,
                emailError: true
            }));
            enqueueSnackbar('Ingresa un correo electrónico válido.', {
                variant: 'error'
            });
            return;
        }

        setIsLoading(true);
        const result = await userForgotPassword(email);
        setIsLoading(false);

        if (result.result) {
            enqueueSnackbar(result.message, {
                variant: 'success'
            });
            setShowForgotPassword(false);
            setShowForgotPasswordReset(true);
            return;
        }

        enqueueSnackbar(result.message || 'No se pudo enviar el código de recuperación.', {
            variant: 'error'
        });
    }

    const OnSubmitForgotPasswordReset = async () => {
        if (isLoading) {
            return;
        }

        const currentErrors = {
            forgotCodeError: !forgotPasswordForm.code,
            forgotNewPasswordError: !forgotPasswordForm.newPassword,
            forgotConfirmPasswordError: !forgotPasswordForm.confirmPassword || forgotPasswordForm.newPassword !== forgotPasswordForm.confirmPassword
        };

        if (Object.values(currentErrors).some(Boolean)) {
            setErrors((prev) => ({
                ...prev,
                ...currentErrors
            }));
            enqueueSnackbar('Completa correctamente los campos para restablecer la contraseña.', {
                variant: 'error'
            });
            return;
        }

        setIsLoading(true);
        const result = await userConfirmForgotPassword({
            email,
            code: forgotPasswordForm.code,
            password: forgotPasswordForm.newPassword
        });
        setIsLoading(false);

        if (result.result) {
            enqueueSnackbar(result.message, {
                variant: 'success'
            });
            setForgotPasswordForm({
                code: '',
                newPassword: '',
                confirmPassword: ''
            });
            setUserForm((prev) => ({
                ...prev,
                password: ''
            }));
            setShowForgotPasswordReset(false);
            return;
        }

        enqueueSnackbar(result.message || 'No se pudo restablecer la contraseña.', {
            variant: 'error'
        });
    }


    return (
        <>
            <Box sx={{ position: 'relative', width: '100%', background: 'black', minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: { md: 'center' } }}>
                <Box sx={{
                    position: 'absolute', top: 0, left: { xs: 0, lg: '0' }, display: { xs: 'none', md: 'block' },
                    width: { xs: '100%', md: '37%', lg: '40%' }, height: '100%', zIndex: '0'
                }}>
                    <ShapeRightIcon leftDireccion={true} />
                </Box>
                <Box sx={{
                    position: 'absolute', top: 0, right: { xs: 0, lg: '0' }, overflow: 'hidden',
                    width: { xs: '100%', md: '37%', lg: '40%' }, height: '100%', zIndex: '0'
                }}>
                    <ShapeRightIcon />
                </Box>
                <Box sx={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', pt: { xs: '30%', md: 0 },
                    px: { xs: '1.5rem', sm: '3rem', md: '7.75rem', lg: '20rem', xl: '28.94rem' }
                }}>
                    <Box sx={{ width: { xs: '12.0625rem', md: '15.125rem' } }}>
                        <LogoClient customStyle={{ height: '100%', width: '100%' }} />
                    </Box>
                    {
                        showVerificationEmail ? (
                            <Box width={'100%'} pb={'3rem'} pt={{ xs: '2.23rem', md: '5.3rem' }}>
                                <Box sx={{ px: { xs: '0.91rem', sm: '5rem', md: '9rem', lg: '5rem', xl: '9rem' } }}>
                                    <Typography sx={{ fontSize: { xs: '1.75rem', md: '2rem' }, fontWeight: 'bold', color: '#fff', textAlign: 'center', pb: '2rem' }}>
                                        Verifica tu correo electrónico
                                    </Typography>
                                    <Typography sx={{ fontSize: { xs: '0.875rem', md: '1rem' }, color: '#fff', textAlign: 'center', pt: '1rem', pb: '1rem' }}>
                                        Ingresa el código de verificación que enviamos a tu correo electrónico.
                                    </Typography>
                                    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'center', width: '60%' }}>
                                            <FormControl variant="standard" fullWidth sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                <VerificationCodeInput
                                                    value={emailVerificationCode}
                                                    error={emailVerificationError}
                                                    disabled={!showVerificationEmail}
                                                    onChange={setEmailVerificationCode}
                                                />
                                            </FormControl>
                                        </Box>
                                    </Box>
                                    <Box sx={{ pt: '2rem', pb: '1rem', display: 'flex', justifyContent: 'center', px: { xs: '2.63rem', md: '3.44rem', lg: '9.44rem' } }}>
                                        <ButtonStyled isDisabled={isLoading} text='Verificar' handleClick={OnSubmitVerificationForm} customStyle={{ padding: { xs: '1rem 2rem' }, width: '50%' }} />
                                    </Box>
                                </Box>
                                <Box sx={{ pt: '2rem' }}>
                                    <Divider sx={{ width: '100%', borderWidth: '1px', '::before, ::after': { borderColor: '#D9D9D9' } }}>
                                        <Typography sx={{ color: '#fff', fontSize: '0.875rem' }}>¿No recibiste el código?</Typography>
                                    </Divider>
                                    <Box sx={{ pt: '1rem', display: 'flex', justifyContent: 'center', px: { xs: '2.63rem', md: '3.44rem', lg: '9.44rem' } }}>
                                        <Button variant="outlined"
                                            sx={{
                                                textTransform: 'none', padding: { xs: '1rem 2rem' }, width: '100%', color: '#FFF',
                                                borderRadius: '0.25rem', border: '1px solid #FC581F', fontWeight: 'bold',
                                                '&:hover': {
                                                    borderColor: '#FFF'
                                                },
                                            }}
                                            onClick={OnRequestNewCode}
                                        >
                                            Solicitar un nuevo código
                                        </Button>
                                    </Box>
                                </Box>
                            </Box>
                        ) : showForgotPassword ? (
                            <Box width={'100%'} pb={'3rem'} pt={{ xs: '2.23rem', md: '5.3rem' }}>
                                <Box sx={{ px: { xs: '0.91rem', sm: '5rem', md: '9rem', lg: '5rem', xl: '9rem' } }}>
                                    <Typography sx={{ fontSize: { xs: '1.75rem', md: '2rem' }, fontWeight: 'bold', color: '#fff', textAlign: 'center', pb: '2rem' }}>
                                        Recuperar contraseña
                                    </Typography>
                                    <Typography sx={{ fontSize: { xs: '0.875rem', md: '1rem' }, color: '#fff', textAlign: 'center', pt: '1rem', pb: '2rem' }}>
                                        Ingresa tu correo electrónico para recibir un código de recuperación.
                                    </Typography>
                                    <Grid container rowSpacing={{ xs: '1.75rem', }} columnSpacing={{ xs: '0rem' }}>
                                        <Grid item xs={12}>
                                            <FormControl variant="standard" fullWidth sx={formControlStyle}>
                                                <FormLabel sx={formLabelStyle}>
                                                    CORREO ELECTRÓNICO:
                                                </FormLabel>
                                                <TextField
                                                    inputProps={inputStyle}
                                                    error={emailError}
                                                    placeholder='Escribe tu correo electrónico'
                                                    size='small'
                                                    name='email'
                                                    value={email}
                                                    onChange={updateForm}
                                                />
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                    <Box sx={{ pt: '1.75rem' }}>
                                        <ButtonStyled isDisabled={isLoading} text={'Enviar código'} handleClick={OnSubmitForgotPasswordRequest} customStyle={{ padding: { xs: '1rem 2rem' }, width: '100%', fontWeight: 'bold' }} />
                                    </Box>
                                    <Box sx={{ pt: '1rem' }}>
                                        <Button variant="text"
                                            sx={{
                                                textTransform: 'none', width: '100%', color: '#FFF', textDecoration: 'underline'
                                            }}
                                            onClick={OnCloseForgotPassword}
                                        >
                                            Volver a inicio de sesión
                                        </Button>
                                    </Box>
                                </Box>
                            </Box>
                        ) : showForgotPasswordReset ? (
                            <Box width={'100%'} pb={'3rem'} pt={{ xs: '2.23rem', md: '5.3rem' }}>
                                <Box sx={{ px: { xs: '0.91rem', sm: '5rem', md: '9rem', lg: '5rem', xl: '9rem' } }}>
                                    <Typography sx={{ fontSize: { xs: '1.75rem', md: '2rem' }, fontWeight: 'bold', color: '#fff', textAlign: 'center', pb: '2rem' }}>
                                        Restablecer contraseña
                                    </Typography>
                                    <Grid container rowSpacing={{ xs: '1.75rem', }} columnSpacing={{ xs: '0rem' }}>
                                        <Grid item xs={12}>
                                            <FormControl variant="standard" fullWidth sx={formControlStyle}>
                                                <FormLabel sx={formLabelStyle}>
                                                    CÓDIGO DE RECUPERACIÓN:
                                                </FormLabel>
                                                <TextField
                                                    inputProps={inputStyle}
                                                    error={forgotCodeError}
                                                    placeholder='Escribe el código'
                                                    size='small'
                                                    name='code'
                                                    value={forgotPasswordForm.code}
                                                    onChange={updateForgotPasswordForm}
                                                />
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <FormControl variant="standard" fullWidth sx={formControlStyle}>
                                                <FormLabel sx={formLabelStyle}>
                                                    NUEVA CONTRASEÑA:
                                                </FormLabel>
                                                <OutlinedInput
                                                    inputProps={inputStyle}
                                                    fullWidth
                                                    size="small"
                                                    type={showForgotNewPassword ? 'text' : 'password'}
                                                    name='newPassword'
                                                    value={forgotPasswordForm.newPassword}
                                                    placeholder='********'
                                                    onChange={updateForgotPasswordForm}
                                                    error={forgotNewPasswordError}
                                                    endAdornment={
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                onClick={() => setShowForgotNewPassword((show) => !show)}
                                                                edge="end"
                                                            >
                                                                {showForgotNewPassword ? <VisibilityOff /> : <Visibility />}
                                                            </IconButton>
                                                        </InputAdornment>
                                                    }
                                                />
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <FormControl variant="standard" fullWidth sx={formControlStyle}>
                                                <FormLabel sx={formLabelStyle}>
                                                    CONFIRMAR CONTRASEÑA:
                                                </FormLabel>
                                                <OutlinedInput
                                                    inputProps={inputStyle}
                                                    fullWidth
                                                    size="small"
                                                    type={showForgotConfirmPassword ? 'text' : 'password'}
                                                    name='confirmPassword'
                                                    value={forgotPasswordForm.confirmPassword}
                                                    placeholder='********'
                                                    onChange={updateForgotPasswordForm}
                                                    error={forgotConfirmPasswordError}
                                                    endAdornment={
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                onClick={() => setShowForgotConfirmPassword((show) => !show)}
                                                                edge="end"
                                                            >
                                                                {showForgotConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                                            </IconButton>
                                                        </InputAdornment>
                                                    }
                                                />
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                    <Box sx={{ pt: '1.75rem' }}>
                                        <ButtonStyled isDisabled={isLoading} text={'Restablecer contraseña'} handleClick={OnSubmitForgotPasswordReset} customStyle={{ padding: { xs: '1rem 2rem' }, width: '100%', fontWeight: 'bold' }} />
                                    </Box>
                                    <Box sx={{ pt: '1rem' }}>
                                        <Button variant="text"
                                            sx={{
                                                textTransform: 'none', width: '100%', color: '#FFF', textDecoration: 'underline'
                                            }}
                                            onClick={OnSubmitForgotPasswordRequest}
                                        >
                                            Reenviar código
                                        </Button>
                                    </Box>
                                    <Box>
                                        <Button variant="text"
                                            sx={{
                                                textTransform: 'none', width: '100%', color: '#FFF', textDecoration: 'underline'
                                            }}
                                            onClick={OnCloseForgotPassword}
                                        >
                                            Volver a inicio de sesión
                                        </Button>
                                    </Box>
                                </Box>
                            </Box>
                        ) : (<>
                            <Box width={'100%'} pb={'3rem'} pt={{ xs: '2.23rem', md: '5.3rem' }}>
                                <Box sx={{ px: { xs: '0.91rem', sm: '5rem', md: '9rem', lg: '5rem', xl: '9rem' } }}>
                                    <Typography sx={{ fontSize: { xs: '1.75rem', md: '2rem' }, fontWeight: 'bold', color: '#fff', textAlign: 'center', pb: '3rem' }}>Inicio de sesión</Typography>
                                    <Grid container rowSpacing={{ xs: '1.75rem', }} columnSpacing={{ xs: '0rem' }}>
                                        <Grid item xs={12}>
                                            <FormControl variant="standard" fullWidth sx={formControlStyle}>
                                                <FormLabel sx={formLabelStyle}>
                                                    CORREO ELECTRÓNICO:
                                                </FormLabel>
                                                <TextField
                                                    inputProps={inputStyle}
                                                    error={emailError}
                                                    placeholder='Escribe tu correo electrónico'
                                                    size='small'
                                                    name='email'
                                                    value={email}
                                                    onChange={updateForm}
                                                />
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <FormControl variant="standard" fullWidth sx={formControlStyle} >
                                                <FormLabel focused={false} sx={formLabelStyle}>
                                                    CONTRASEÑA:
                                                </FormLabel>
                                                <OutlinedInput
                                                    inputProps={inputStyle}
                                                    fullWidth
                                                    size="small"
                                                    type={showPassword ? 'text' : 'password'}
                                                    name='password'
                                                    value={password}
                                                    placeholder='********'
                                                    onChange={updateForm}
                                                    error={passwordError}
                                                    endAdornment={
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                onClick={handleClickShowPassword}
                                                                edge="end"
                                                            >
                                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                                            </IconButton>
                                                        </InputAdornment>
                                                    }
                                                />
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                    <Box sx={{ pt: '1.75rem' }}>
                                        <ButtonStyled text={'Iniciar sesión'} handleClick={OnSubmitForm} customStyle={{ padding: { xs: '1rem 2rem' }, width: '100%', fontWeight: 'bold' }} />
                                    </Box>
                                    <Box sx={{ pt: '1rem' }}>
                                        <Button variant="text"
                                            sx={{
                                                textTransform: 'none', width: '100%', color: '#FFF', textDecoration: 'underline'
                                            }}
                                            onClick={OnOpenForgotPassword}
                                        >
                                            ¿Olvidaste tu contraseña?
                                        </Button>
                                    </Box>
                                </Box>
                            </Box>
                            <Divider sx={{ width: '100%', borderWidth: '1px', '::before, ::after': { borderColor: '#D9D9D9' } }}>
                                <Typography sx={{ color: '#fff', fontSize: '0.875rem' }}>¿Eres nuevo en medición total?</Typography>
                            </Divider>
                            <Box width={'100%'}>
                                <Box pt={'1.75rem'} pb={'1rem'} sx={{ px: { xs: '0.91rem', sm: '5rem', md: '9rem', lg: '5rem', xl: '9rem' } }}>
                                    <Button variant="outlined"
                                        sx={{
                                            textTransform: 'none', padding: { xs: '1rem 2rem' }, width: '100%', color: '#FFF',
                                            borderRadius: '0.25rem', border: '1px solid #FC581F', fontWeight: 'bold',
                                            '&:hover': {
                                                borderColor: '#FFF'
                                            },
                                        }}
                                        onClick={() => { navigate('/user/new') }}
                                    >
                                        Crea tu cuenta de medición total
                                    </Button>
                                </Box>
                            </Box>
                        </>)
                    }
                </Box>
            </Box>
        </>
    )
}

export default LoginPage